/**
 * The API's own limits and options, fetched once.
 *
 * Copy kept drifting from what the API actually enforces — a composer that
 * said one thing while the API refused another. Rendering from this store
 * means the screen and the rule cannot disagree.
 *
 * Falls back to sensible values if the fetch fails, so a blip degrades the
 * hints rather than the app: the API validates every one of these anyway.
 */
import { create } from "zustand";
import { appConfig } from "../api/endpoints";
import { TERMS_VERSION } from "../components/ContributorTerms";
import type { AppConfig } from "../types/api";

const FALLBACK: AppConfig = {
  recording: {
    min_seconds: 3,
    max_seconds: 60,
    ideal_min_seconds: 10,
    ideal_max_seconds: 40,
    max_bytes: 25 * 1024 * 1024,
    allowed_types: ["audio/aac", "audio/mp4", "audio/mpeg", "audio/wav"],
  },
  text_response: { max_length: 1000 },
  compensation: {
    currency: "USD",
    rate: { cents: 50, currency: "USD", formatted: "0.50" },
    payout_threshold: { cents: 2000, currency: "USD", formatted: "20.00" },
    payout_minimum: { cents: 500, currency: "USD", formatted: "5.00" },
  },
  assignment: { target: 100 },
  genders: [
    { id: "male", label: "Male", available: true },
    { id: "female", label: "Female", available: true },
  ],
  countries: [],
  // Kept in step with the terms the app actually renders, so the fallback
  // never claims a version that no longer exists.
  terms_version: TERMS_VERSION,
  invite_code_required: false,
  application: { invite_days: 15 },
  // A fallback only. The real list is served, so adding a currency is a
  // config change rather than a release.
  payout: {
    method: "wise",
    currencies: ["USD", "EUR", "GBP", "PHP", "INR"],
  },
};

interface ConfigState {
  config: AppConfig;
  loaded: boolean;
  load: () => Promise<void>;
}

export const useConfig = create<ConfigState>((set, get) => ({
  config: FALLBACK,
  loaded: false,

  load: async () => {
    if (get().loaded) return;
    try {
      // Over the fallback rather than instead of it: the web app and the API
      // deploy separately, so a key the client knows about can be missing
      // from an API that has not caught up yet.
      set({ config: { ...FALLBACK, ...(await appConfig.get()) }, loaded: true });
    } catch {
      // Keep the fallback; every value here is enforced server-side anyway.
      set({ loaded: true });
    }
  },
}));
